import { NavLink, useLocation } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import {
  LayoutDashboard, KanbanSquare, Users, PhoneCall, MessageSquare, CalendarClock, BarChart3,
  Repeat, UsersRound, Inbox, Bot, MapPin, Brain, Rocket, Target, Send, Sparkles, LineChart,
  ChevronDown, Menu, Upload, Sun, Moon, LogOut, Mail,
} from "lucide-react";

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/attention", label: "Attention", icon: Target, id: "attention" },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, id: "pipeline" },
  { label: "Leads", icon: Users, id: "leads-group", items: [
    { to: "/leads", label: "All Leads", icon: Users, id: "leads" },
    { to: "/find-leads", label: "Find Leads", icon: MapPin, id: "find-leads" },
  ] },
  { label: "Outreach", icon: Send, id: "outreach", items: [
    { to: "/inbox", label: "Inbox", icon: Inbox, id: "inbox" },
    { to: "/call-desk", label: "Calls", icon: PhoneCall, id: "call-desk" },
    { to: "/sms", label: "SMS", icon: MessageSquare, id: "sms" },
    { to: "/followups", label: "Follow-ups", icon: CalendarClock, id: "followups" },
    { to: "/sequences", label: "Sequences", icon: Repeat, id: "sequences" },
  ] },
  { label: "Automation", icon: Sparkles, id: "automation", items: [
    { to: "/ai-agent", label: "AI Agent", icon: Bot, id: "ai-agent" },
    { to: "/orchestrator", label: "Orchestrator", icon: Brain, id: "orchestrator" },
  ] },
  { label: "Insights", icon: LineChart, id: "insights", items: [
    { to: "/launch", label: "Launch", icon: Rocket, id: "launch" },
    { to: "/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
    { to: "/team", label: "Team", icon: UsersRound, id: "team", adminOnly: true },
  ] },
];

const linkCls = (active) =>
  `group flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium outline-none transition-colors ${
    active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
  }`;

const visible = (items, isAdmin) => items.filter((n) => !n.adminOnly || isAdmin);

const NavGroup = ({ g, isAdmin }) => {
  const { pathname } = useLocation();
  const items = visible(g.items, isAdmin);
  const active = items.some((i) => pathname.startsWith(i.to));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={`${linkCls(active)} data-[state=open]:bg-secondary`} data-testid={`nav-group-${g.id}`}>
        <g.icon className="h-4 w-4 hidden xl:block" />
        {g.label}
        <ChevronDown className="h-3.5 w-3.5 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="w-52">
        {items.map((n) => (
          <DropdownMenuItem key={n.to} asChild>
            <NavLink to={n.to} data-testid={`nav-${n.id}-link`}
              className={({ isActive }) => `flex items-center gap-2.5 cursor-pointer ${isActive ? "text-primary" : ""}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DesktopNav = ({ isAdmin }) => (
  <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1" data-testid="desktop-nav">
    {NAV.map((n) =>
      n.items ? (
        <NavGroup key={n.id} g={n} isAdmin={isAdmin} />
      ) : (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.id}-link`} className={({ isActive }) => linkCls(isActive)}>
          <n.icon className="h-4 w-4 hidden xl:block" />
          {n.label}
        </NavLink>
      )
    )}
  </nav>
);

export const AccountMenu = ({ user, theme, onToggleTheme, onImport, onEmailSettings, onLogout }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <Button size="icon" variant="ghost" className="hidden lg:flex" data-testid="account-menu-trigger">
        <Menu className="h-5 w-5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={8} className="w-60" data-testid="account-menu">
      <DropdownMenuLabel className="flex items-center gap-2.5 font-normal">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold uppercase">
          {(user?.name || user?.email || "?").slice(0, 2)}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium truncate">{user?.name || "Account"}</span>
          <span className="block text-xs text-muted-foreground truncate" data-testid="account-email">{user?.email}</span>
        </span>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onImport} className="gap-2.5 cursor-pointer" data-testid="import-csv-button">
        <Upload className="h-4 w-4" /> Import CSV
      </DropdownMenuItem>
      {user?.role === "admin" && (
        <DropdownMenuItem onSelect={onEmailSettings} className="gap-2.5 cursor-pointer" data-testid="email-settings-button">
          <Mail className="h-4 w-4" /> Email settings
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onToggleTheme(); }} className="gap-2.5 cursor-pointer" data-testid="theme-toggle">
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        {theme === "dark" ? "Light mode" : "Dark mode"}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onLogout} className="gap-2.5 cursor-pointer text-rose-400 focus:text-rose-400" data-testid="logout-button">
        <LogOut className="h-4 w-4" /> Log out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const MobileLink = ({ n, onClose }) => (
  <NavLink to={n.to} end={n.end} onClick={onClose} data-testid={`mobile-nav-${n.id}-link`}
    className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
    <n.icon className="h-4 w-4" /> {n.label}
  </NavLink>
);

const mobileBtn = "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary";

export const MobileNav = ({ isAdmin, onClose, theme, onToggleTheme, onImport, onEmailSettings, onLogout }) => (
  <nav className="lg:hidden pb-4 space-y-3 max-h-[75vh] overflow-y-auto" data-testid="mobile-nav">
    <div className="grid grid-cols-2 gap-1">
      {NAV.filter((n) => !n.items).map((n) => <MobileLink key={n.to} n={n} onClose={onClose} />)}
    </div>
    {NAV.filter((n) => n.items).map((g) => (
      <div key={g.id}>
        <p className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{g.label}</p>
        <div className="grid grid-cols-2 gap-1">
          {visible(g.items, isAdmin).map((n) => <MobileLink key={n.to} n={n} onClose={onClose} />)}
        </div>
      </div>
    ))}
    <div className="grid grid-cols-2 gap-1 border-t border-border pt-3">
      <button onClick={() => { onClose(); onImport(); }} className={mobileBtn} data-testid="mobile-import-csv">
        <Upload className="h-4 w-4" /> Import CSV
      </button>
      {isAdmin && (
        <button onClick={() => { onClose(); onEmailSettings(); }} className={mobileBtn} data-testid="mobile-email-settings">
          <Mail className="h-4 w-4" /> Email settings
        </button>
      )}
      <button onClick={onToggleTheme} className={mobileBtn} data-testid="mobile-theme-toggle">
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {theme === "dark" ? "Light mode" : "Dark mode"}
      </button>
      <button onClick={onLogout} className={`${mobileBtn} text-rose-400`} data-testid="mobile-logout">
        <LogOut className="h-4 w-4" /> Log out
      </button>
    </div>
  </nav>
);
